// ============================================
// Consent Section
// CalAIM Services Consent (CSS Consent Form)
// Exact match to CSS_Consent_Form.pdf
// ============================================

import React from 'react';
import { useFormContext } from 'react-hook-form';
import { FormSubmission } from '../../types';
import FormField from '../ui/FormField';
import { useLanguage } from '../../i18n/LanguageContext';

const ConsentSection: React.FC = () => {
  const { register, watch, formState: { errors } } = useFormContext<FormSubmission>();
  const { t } = useLanguage();
  const isMinor = watch('consent.isMinor');
  const signingOnBehalf = watch('consent.signingOnBehalf');

  return (
    <div className="space-y-8">
      <p className="text-sm text-gray-600 bg-blue-50 p-4 rounded-lg">
        {t('consent.autoFillNotice')}
      </p>

      {/* Minor Indicator */}
      <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
        <label className="flex items-center space-x-3 cursor-pointer">
          <input
            type="checkbox"
            {...register('consent.isMinor')}
            className="form-checkbox h-5 w-5 text-yellow-600 rounded"
          />
          <span className="text-gray-900 font-medium">
            {t('consent.minorIndicator')}
          </span>
        </label>
        {isMinor && (
          <p className="text-sm text-yellow-800 mt-2 ml-8">
            {t('consent.minorNote')}
          </p>
        )}
      </div>

      {/* Notice to Medi-Cal Beneficiary */}
      <div className="bg-white p-6 rounded-lg border border-gray-300">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('consent.noticeHeading')}</h3>
        
        <p className="text-gray-800 mb-4">
          {t('consent.authorizationIntro')}
        </p>

        <ul className="list-disc ml-6 mb-4 text-gray-800 space-y-2">
          <li>{t('consent.ecmService')}</li>
          <li>{t('consent.csServices')}</li>
        </ul>

        <p className="text-gray-900 font-semibold mb-3">{t('consent.understandHeading')}</p>

        <ul className="list-disc ml-6 mb-4 text-gray-800 space-y-2">
          <li>{t('consent.understand1')}</li>
          <li>{t('consent.understand2')}</li>
          <li>{t('consent.understand3')}</li>
          <li>{t('consent.understand4')}</li>
          <li>{t('consent.understand5')}</li>
        </ul>

        <p className="text-gray-800 mb-4">
          {t('consent.withdrawalInfo')}
        </p>

        <p className="text-gray-800">
          {t('consent.accuracyStatement')}
        </p>
      </div>

      {/* Signature Note */}
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <p className="text-sm text-blue-900">
          {t('consent.signatureNote')}
        </p>
      </div>

      {/* Signing on Behalf Section */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {t('consent.authorizedRepHeading')}
        </h3>
        
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
          <label className="flex items-start space-x-3 cursor-pointer mb-4">
            <input
              type="checkbox"
              {...register('consent.signingOnBehalf')}
              className="form-checkbox h-5 w-5 text-blue-600 rounded mt-0.5"
            />
            <div>
              <span className="text-gray-900 font-medium">
                {t('consent.signingOnBehalf')}
              </span>
              <p className="text-sm text-gray-600 mt-1">
                {t('consent.signingOnBehalfDesc')}
              </p>
            </div>
          </label>

          {signingOnBehalf && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <FormField
                label={t('consent.printedName')}
                error={errors.consent?.printedName?.message}
                required
              >
                <input
                  type="text"
                  {...register('consent.printedName', {
                    required: signingOnBehalf ? t('consent.printedNameRequired') : false
                  })}
                  className="form-input w-full rounded-md border-gray-300"
                  placeholder={t('consent.printedNamePlaceholder')}
                />
              </FormField>

              <FormField
                label={t('consent.relationshipToMember')}
                error={errors.consent?.relationshipToMember?.message}
                required
              >
                <input
                  type="text"
                  {...register('consent.relationshipToMember', {
                    required: signingOnBehalf ? t('consent.relationshipRequired') : false
                  })}
                  className="form-input w-full rounded-md border-gray-300"
                  placeholder={t('consent.relationshipPlaceholder')}
                />
              </FormField>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConsentSection;
